import _ from 'lodash'
import { TaxProcessingOrderOptions } from '../../utils/constants'

const complexTaxTerritoryKeys = ['cityTax', 'countyTax', 'stateTax', 'federalTax']

const booleanToLabel = (value) => (value ? 'Enabled' : 'Disabled')

const trimTrailingZeros = (valueAsString) => valueAsString.replace(/\.?0+$/, '')

const formatPercentFromDecimal = (value, precision = 4) => {
  if (value === null || value === undefined || value === '') return '-'
  const parsed = Number(value)
  if (Number.isNaN(parsed)) return `${value}`
  return `${trimTrailingZeros((parsed * 100).toFixed(precision))}%`
}

const formatPercentValue = (value, precision = 4) => {
  if (value === null || value === undefined || value === '') return '-'
  const parsed = Number(value)
  if (Number.isNaN(parsed)) return `${value}`
  return `${trimTrailingZeros(parsed.toFixed(precision))}%`
}

const formatPlainValue = (value) => {
  if (value === null || value === undefined || value === '') return '-'
  if (typeof value === 'boolean') return booleanToLabel(value)
  return `${value}`
}

const formatTaxOrder = (value) => {
  if (!value) return '-'
  return TaxProcessingOrderOptions[value] || value
}

const formatExciseType = (value) => {
  if (!value) return '-'
  if (value === 'PER_ITEM') return 'Line Item'
  if (value === 'TOTAL_AMOUNT') return 'Included In Total Amount'
  return value
}

const addChangeIfDifferent = (
  changes,
  label,
  before,
  after,
  formatter = formatPlainValue,
  metadata = null,
) => {
  if (_.isEqual(before, after)) return
  changes.push({
    label,
    before: formatter(before),
    after: formatter(after),
    ...(metadata ? { metadata } : {}),
  })
}

export const buildTaxOptionChanges = (
  beforeData = {},
  afterData = {},
  beforeSettings = {},
  afterSettings = {},
) => {
  const changes = []

  const fields = [
    {
      path: 'useComplexTax',
      label: 'Tax Option',
      format: (value) => (value ? 'Complex' : 'Regular'),
    },
    { path: 'taxOrder', label: 'Tax Processing Order', format: formatTaxOrder },
    { path: 'taxInfo.cityTax', label: 'City Tax', format: formatPercentFromDecimal },
    { path: 'taxInfo.countyTax', label: 'County Tax', format: formatPercentFromDecimal },
    { path: 'taxInfo.stateTax', label: 'State Tax', format: formatPercentFromDecimal },
    { path: 'taxInfo.federalTax', label: 'Federal Tax', format: formatPercentFromDecimal },
    {
      path: 'taxInfo.activeCDTFATax',
      label: 'Enable CDTFA State Tax',
      format: booleanToLabel,
    },
    { path: 'enableExciseTax', label: 'Includes California Excise Tax', format: booleanToLabel },
    { path: 'exciseTaxType', label: 'Excise Tax Receipt Display', format: formatExciseType },
    {
      path: 'exciseTaxInfo.exciseTax',
      label: 'State Excise Tax',
      format: (value) => formatPercentValue(value, 2),
    },
    {
      path: 'nalExciseFromRetailCost',
      label: 'Non-Arms Length on Retail Price',
      format: booleanToLabel,
    },
    {
      path: 'nalExciseAfterCityTax',
      label: 'Calculate Excise Tax after City Tax',
      format: booleanToLabel,
    },
    { path: 'alExciseOnZeroPrice', label: 'Arms Length Zero Price', format: booleanToLabel },
    {
      path: 'productPriceIncludeExciseTax',
      label: 'Product Prices Include Excise Tax - Default',
      format: booleanToLabel,
    },
    {
      path: 'enableSurchargeTaxExciseTax',
      label: 'Surcharge Fee Subjected to Excise Tax',
      format: booleanToLabel,
    },
    {
      path: 'enableDeliveryFeeBeforeExciseTax',
      label: 'Calculate Delivery Fee Tax before Excise Tax',
      format: booleanToLabel,
      inSettings: true,
    },
    {
      path: 'enableSurchargeFeeBeforeExciseTax',
      label: 'Calculate Surcharge Fee Tax before Excise Tax',
      format: booleanToLabel,
      inSettings: true,
    },
  ]

  fields.forEach(({ path, label, format, inSettings }) => {
    const previousValue = inSettings ? _.get(beforeSettings, path) : _.get(beforeData, path)
    const currentValue = inSettings ? _.get(afterSettings, path) : _.get(afterData, path)
    addChangeIfDifferent(changes, label, previousValue, currentValue, format)
  })

  return changes
}

export const buildTaxDisplayNameChanges = (
  beforeData = {},
  afterData = {},
  taxTypeOptions = [],
) => {
  const changes = []
  const taxTypeLabelByValue = taxTypeOptions.reduce((acc, item) => {
    acc[item.value] = item.label
    return acc
  }, {})

  const fields = [
    { path: 'name', label: 'Tax Display Name' },
    {
      path: 'appendTax',
      label: 'Append "Tax" to Tax Name',
      format: booleanToLabel,
    },
    {
      path: 'taxType',
      label: 'Tax Type Mapping',
      format: (value) => (value === 'none' ? 'None' : taxTypeLabelByValue[value] || value),
      fallback: 'none',
    },
  ]

  fields.forEach(({ path, label, format, fallback }) => {
    const beforeValue = _.get(beforeData, path) ?? fallback
    const afterValue = _.get(afterData, path) ?? fallback
    addChangeIfDifferent(changes, label, beforeValue, afterValue, format)
  })

  return changes
}

export const buildComplexTaxChanges = (beforeData = {}, afterData = {}) => {
  const changes = []
  const territoryFieldMap = [
    { key: 'displayName', label: 'Display Name' },
    { key: 'taxRate', label: 'Tax Rate', format: formatPercentValue },
    { key: 'compound', label: 'Compound', format: booleanToLabel },
    { key: 'taxOrder', label: 'Tax Order', format: formatTaxOrder },
    { key: 'active', label: 'Active', format: booleanToLabel },
    {
      key: 'calculateTaxOnPreDiscount',
      label: 'Tax on Subtotal',
      format: booleanToLabel,
    },
    { key: 'activeExciseTax', label: 'Tax Excise', format: booleanToLabel },
    { key: 'activeDeliveryFeeTax', label: 'Tax Delivery Fee', format: booleanToLabel },
    { key: 'activeSurchargeTax', label: 'Tax Payment Fee', format: booleanToLabel },
    { key: 'activeCDTFATax', label: 'CDTFA Tax', format: booleanToLabel },
  ]

  complexTaxTerritoryKeys.forEach((territoryKey) => {
    const beforeTerritory = beforeData[territoryKey] || {}
    const afterTerritory = afterData[territoryKey] || {}
    const territoryLabel = afterTerritory.territory || beforeTerritory.territory || territoryKey

    territoryFieldMap.forEach(({ key, label, format }) => {
      addChangeIfDifferent(
        changes,
        `${territoryLabel} Tax - ${label}`,
        beforeTerritory[key],
        afterTerritory[key],
        format || formatPlainValue,
        {
          territory: territoryLabel,
          fieldLabel: label,
        },
      )
    })
  })

  return changes
}



// WEBPACK FOOTER //
// ./src/components/TaxOption/taxChangeUtils.js